import { PERIOD_COLORS, TRADITION_LAYERS } from "./config";

function selectedTraditions(state) {
  return new Set(
    TRADITION_LAYERS.filter(({ slug }) => state[slug]).map(({ slug }) => slug)
  );
}

function selectedFigures(figures, state) {
  return figures.filter((figure) => (
    (figure.spine && state.mainstreamFigures) || (!figure.spine && state.allFigures)
  ));
}

function sortRows(left, right) {
  return left.startYear - right.startYear ||
    left.endYear - right.endYear ||
    left.kind.localeCompare(right.kind) ||
    left.name.localeCompare(right.name);
}

function buildSummary(figures, entries, events, state) {
  const noun = figures.length === 1 ? "figure" : "figures";
  const parts = [`${figures.length} occult ${noun}`];

  TRADITION_LAYERS.forEach((layer) => {
    if (!state[layer.slug]) return;
    const count = entries.filter((entry) => entry.tradition === layer.slug).length;
    parts.push(`${count} ${layer.summary}`);
  });

  if (state.events) parts.push(`${events.length} other historical events`);
  return `Showing ${parts.join(", ")}${state.periods ? "; every row coded by period" : ""}.`;
}

export function buildPeriodMap(periods) {
  return new Map(periods.map((period, index) => [
    period.slug,
    { ...period, color: PERIOD_COLORS[index % PERIOD_COLORS.length] }
  ]));
}

export function buildLayerCounts(timeline) {
  const counts = {
    mainstreamFigures: timeline.figures.filter((figure) => figure.spine).length,
    allFigures: timeline.figures.filter((figure) => !figure.spine).length,
    events: timeline.events.length
  };
  TRADITION_LAYERS.forEach(({ slug }) => {
    const entries = timeline.esotericEntries.filter((entry) => entry.tradition === slug);
    const figures = entries.filter((entry) => entry.entryType === "figure").length;
    counts[slug] = { total: entries.length, figures, other: entries.length - figures };
  });
  return counts;
}

export function buildTimelineView(timeline, state) {
  const traditions = selectedTraditions(state);
  const entries = timeline.esotericEntries.filter((entry) => traditions.has(entry.tradition));
  const profileSlugs = new Set(entries.map((entry) => entry.profileSlug).filter(Boolean));
  const canonicalKeys = new Set(entries.map((entry) => entry.canonicalKey || entry.slug));
  const figures = selectedFigures(timeline.figures, state)
    .filter((figure) => !profileSlugs.has(figure.slug));
  const events = state.events
    ? timeline.events.filter((event) => !canonicalKeys.has(event.slug))
    : [];
  const rows = [
    ...figures.map((figure) => ({
      ...figure,
      kind: "figure",
      periodSlug: figure.era,
      periodLabel: figure.eraLabel
    })),
    ...events.map((event) => ({ ...event, kind: "event" })),
    ...entries.map((entry) => ({ ...entry, kind: "esoteric" }))
  ].sort(sortRows);
  const visiblePeriodSlugs = new Set(rows.map((row) => row.periodSlug || row.era));

  return {
    axis: state.biblical || state.egyptian ? timeline.ancientAxis : timeline.axis,
    entries,
    events,
    figures,
    rows,
    selectedTraditions: traditions,
    summary: buildSummary(figures, entries, events, state),
    visiblePeriods: timeline.periods.filter((period) => visiblePeriodSlugs.has(period.slug))
  };
}
